/**
 * After auth: terms → profile/career → (tour overlays the app).
 * Existing users who already have a *server* profile are auto-marked complete
 * on first encounter so we don't re-grill them — only terms (if missing)
 * and the tour still run. Local-only guest drafts still go through the
 * profile step so career entries can be collected.
 *
 * Tour completion is stored on the server profile (tourSeenVersion) so it
 * survives logout. Local prefs mirror it for offline; a one-shot sync pushes
 * a local-only completion up when the account already finished the tour on
 * this device before the server column existed.
 *
 * Onboarding flags come from auth.bootstrap (same query as RequireAuth) so
 * there is no profiles.mine waterfall before the shell paints.
 */
import { useEffect, useRef } from 'react';
import { Navigate, useLocation } from 'react-router';
import { useAuth } from '@/hooks/useAuth';
import { usePrefs } from '@/lib/store';
import { trpc } from '@/lib/trpc';
import {
  effectiveTourSeenVersion,
  rememberPostOnboardingRedirect,
  resolveOnboardingStep,
  TERMS_VERSION,
  TOUR_VERSION,
} from '@/lib/onboarding';
import { BootSplash } from '@/components/BootSplash';
import { ModuleTour } from '@/components/ModuleTour';

const ONBOARDING_PREFIX = '/onboarding';

export function RequireOnboarding({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const { user, onboarding, isLoading: authLoading } = useAuth();
  const { prefs, updatePrefs } = usePrefs();
  const utils = trpc.useUtils();

  const completeMut = trpc.profiles.completeOnboarding.useMutation({
    onSuccess: () => {
      void utils.auth.bootstrap.invalidate();
      void utils.profiles.mine.invalidate();
    },
  });

  const completeTourMut = trpc.profiles.completeTour.useMutation({
    onSuccess: (data) => {
      if (data.success) {
        updatePrefs({ tourSeenVersion: data.version });
        void utils.auth.bootstrap.invalidate();
      }
    },
  });

  const termsVersion = onboarding?.termsVersion ?? user?.termsVersion ?? null;
  const serverOnboardingDone = Boolean(onboarding?.onboardingCompleted);
  const hasServerProfile = Boolean(onboarding?.hasServerProfile);
  const serverTourSeenVersion = onboarding?.tourSeenVersion ?? null;

  // Only true pre-existing server profiles qualify for the legacy stamp — not
  // profiles created moments ago by syncProfile on first authenticated login.
  const initialHadServerProfile = useRef<boolean | null>(null);
  const tourSyncAttemptedForUser = useRef<number | null>(null);
  useEffect(() => {
    initialHadServerProfile.current = null;
    tourSyncAttemptedForUser.current = null;
  }, [user?.id]);
  useEffect(() => {
    if (!authLoading && onboarding && initialHadServerProfile.current === null) {
      initialHadServerProfile.current = hasServerProfile;
    }
  }, [authLoading, onboarding, hasServerProfile, user?.id]);

  const needsLegacyStamp =
    !authLoading &&
    onboarding != null &&
    initialHadServerProfile.current === true &&
    !serverOnboardingDone &&
    hasServerProfile &&
    termsVersion === TERMS_VERSION;

  const legacyStampSucceeded = completeMut.data?.success === true;
  const legacyStampSettled =
    legacyStampSucceeded ||
    completeMut.isError ||
    completeMut.data?.success === false;

  // Legacy accounts: server profile already filled, never stamped — stamp once.
  // Idempotent on the server; React Strict Mode may fire twice.
  useEffect(() => {
    if (!needsLegacyStamp) return;
    if (completeMut.isPending || legacyStampSettled) return;
    completeMut.mutate();
  }, [needsLegacyStamp, completeMut.isPending, legacyStampSettled, completeMut]);

  // Migrate a local-only tour completion to the account (pre-server-column users).
  useEffect(() => {
    if (authLoading || !onboarding || !user?.id) return;
    if (prefs.tourSeenVersion !== TOUR_VERSION) return;
    if (serverTourSeenVersion === TOUR_VERSION) return;
    if (tourSyncAttemptedForUser.current === user.id) return;
    tourSyncAttemptedForUser.current = user.id;
    completeTourMut.mutate({ version: TOUR_VERSION });
  }, [
    authLoading,
    onboarding,
    user?.id,
    prefs.tourSeenVersion,
    serverTourSeenVersion,
    completeTourMut,
  ]);

  // Mirror server → local so prefs stay useful offline after login.
  useEffect(() => {
    if (authLoading) return;
    if (serverTourSeenVersion !== TOUR_VERSION) return;
    if (prefs.tourSeenVersion === TOUR_VERSION) return;
    updatePrefs({ tourSeenVersion: TOUR_VERSION });
  }, [authLoading, serverTourSeenVersion, prefs.tourSeenVersion, updatePrefs]);

  const status = resolveOnboardingStep({
    termsVersion,
    onboardingCompleted: serverOnboardingDone || legacyStampSucceeded,
    tourSeenVersion: effectiveTourSeenVersion(
      serverTourSeenVersion,
      prefs.tourSeenVersion,
    ),
  });

  const onOnboardingRoute = location.pathname.startsWith(ONBOARDING_PREFIX);

  useEffect(() => {
    if (authLoading) return;
    if (status.next !== 'terms' && status.next !== 'profile') return;
    if (onOnboardingRoute) return;
    rememberPostOnboardingRedirect(location.pathname + location.search);
  }, [
    authLoading,
    status.next,
    onOnboardingRoute,
    location.pathname,
    location.search,
  ]);

  if (authLoading) {
    return <BootSplash />;
  }

  // Wait for the legacy stamp so we don't flash the profile form.
  if (needsLegacyStamp && !legacyStampSettled) {
    return <BootSplash />;
  }

  if (status.next === 'terms' && location.pathname !== `${ONBOARDING_PREFIX}/terms`) {
    return <Navigate to={`${ONBOARDING_PREFIX}/terms`} replace />;
  }
  if (status.next === 'profile' && location.pathname !== `${ONBOARDING_PREFIX}/profile`) {
    return <Navigate to={`${ONBOARDING_PREFIX}/profile`} replace />;
  }
  if ((status.next === 'tour' || status.next === 'done') && onOnboardingRoute) {
    return <Navigate to="/" replace />;
  }

  return (
    <>
      {children}
      {status.next === 'tour' && !onOnboardingRoute ? <ModuleTour /> : null}
    </>
  );
}
